import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Empty,
  Image,
  Input,
  Progress,
  Spin,
  Steps,
  Tag,
  Tabs,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  BarcodeOutlined,
  BoxPlotOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  DisconnectOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  SearchOutlined,
  TruckOutlined,
  UserOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Toast } from "primereact/toast";
import config from "../config";
import InspectionFormModal from "./InspectionFormModal";
import "./RTOInboundPage.css";

const { Text, Title } = Typography;

const BASKET_CONFIG = {
  good: {
    label: "Ready to restock",
    shortLabel: "Good",
    helper: "Product and packaging are in sellable condition",
    icon: <CheckCircleOutlined />,
  },
  repacking: {
    label: "Needs repacking",
    shortLabel: "Repack",
    helper: "Product is usable but packaging needs attention",
    icon: <BoxPlotOutlined />,
  },
  damaged: {
    label: "Damaged",
    shortLabel: "Damaged",
    helper: "Product cannot be returned to available stock",
    icon: <ExclamationCircleOutlined />,
  },
  scrap: {
    label: "Scrap",
    shortLabel: "Scrap",
    helper: "Product must be disposed of or written off",
    icon: <DeleteOutlined />,
  },
};

const RTOInboundPage = ({ darkTheme = false }) => {
  const [searchOrderID, setSearchOrderID] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(moment().format("HH:mm:ss"));
  const [isNetworkLost, setIsNetworkLost] = useState(false);
  const [showNetworkBanner, setShowNetworkBanner] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [baskets, setBaskets] = useState({
    available: [],
    good: [],
    repacking: [],
    damaged: [],
    scrap: [],
  });
  const [isInspectionModalVisible, setIsInspectionModalVisible] = useState(false);
  const [inspectionProduct, setInspectionProduct] = useState(null);
  const [inspectionBasketType, setInspectionBasketType] = useState(null);
  const [inspectionSourceType, setInspectionSourceType] = useState("available");
  const toast = useRef(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsNetworkLost(false);
      setShowNetworkBanner(false);
    };
    const handleOffline = () => {
      setIsNetworkLost(true);
      setShowNetworkBanner(true);
      toast.current?.show({
        severity: "warn",
        summary: "Network lost",
        detail: "You are offline. Submission is unavailable until you reconnect.",
        life: 3000,
      });
    };

    setIsNetworkLost(!navigator.onLine);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(moment().format("HH:mm:ss")), 1000);
    return () => clearInterval(timer);
  }, []);

  const notify = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const clearOrder = () => {
    setOrderDetails(null);
    setSearchOrderID("");
    setError(null);
    setBaskets({ available: [], good: [], repacking: [], damaged: [], scrap: [] });
    notify("info", "Order cleared", "Search for another return order to continue.");
  };

  const searchPastOrder = async () => {
    if (!searchOrderID.trim()) {
      notify("warn", "Missing Order ID", "Enter an Order ID to start searching.");
      return;
    }

    setLoading(true);
    setOrderDetails(null);

    try {
      const response = await axios.post(`${config.apiBaseUrl}/orders/past-orders`, {
        orderID: searchOrderID.trim(),
      });
      const order = response.data?.order;

      if (!order) {
        notify("error", "Order not found", "No order was found with this Order ID.");
        return;
      }

      if (order.packed_status !== "Completed") {
        notify("error", "Order not eligible", "This order has not been packed yet. Only orders with a 'Packed' status can be processed for RTO Inbound.");
        return;
      }

      const productMap = new Map();
      (Array.isArray(order.products) ? order.products : []).forEach((product) => {
        const key = product.updated_id || product.sku || product.id;
        if (productMap.has(key)) {
          productMap.get(key).quantity += Number(product.quantity || 0);
        } else {
          productMap.set(key, { ...product, quantity: Number(product.quantity || 0) });
        }
      });

      order.products = Array.from(productMap.values());
      const available = order.products.map((product, index) => ({
        ...product,
        uniqueId: `${product.updated_id || product.sku || product.id}-${index}`,
        sourceType: 'available',
      }));

      setOrderDetails(order);
      setBaskets({ available, good: [], repacking: [], damaged: [], scrap: [] });
      notify("success", "Order loaded", `${order.orderID || searchOrderID} is ready for inspection.`);
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Unable to load this order.";
      notify("error", "Search failed", message);
    } finally {
      setLoading(false);
    }
  };

  const openInspection = (product, targetType, sourceType = "available") => {
    if (sourceType === targetType) return;
    setInspectionProduct(product);
    setInspectionBasketType(targetType);
    setInspectionSourceType(sourceType);
    setIsInspectionModalVisible(true);
  };

  const handleDragStart = (event, product, sourceType) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("productUniqueId", product.uniqueId);
    event.dataTransfer.setData("sourceBasket", sourceType);
  };

  const handleDropInBasket = (event, targetType) => {
    event.preventDefault();
    const sourceType = event.dataTransfer.getData("sourceBasket");
    const uniqueId = event.dataTransfer.getData("productUniqueId");
    const product = (baskets[sourceType] || []).find((item) => item.uniqueId === uniqueId);
    if (product) openInspection(product, targetType, sourceType);
  };

  const handleInspectionSave = (product, targetType, formData) => {
    const quantityReceived = Number(formData.quantityReceived || product.quantity || 0);
    const remainingQuantity = (Number(product.quantity) || 0) - quantityReceived;
    const sourceBucket = inspectionSourceType || "available";
    const targetBucket = targetType;

    setBaskets((previous) => {
      const next = {};
      Object.keys(previous).forEach((key) => {
        next[key] = previous[key].filter((item) => item.uniqueId !== product.uniqueId);
      });

      const movedProduct = {
        ...product,
        quantity: quantityReceived,
        uniqueId: `${product.uniqueId}-${targetBucket}-${Date.now()}`,
        sourceType: targetBucket,
        inspectionData: formData,
      };
      next[targetBucket] = [...next[targetBucket], movedProduct];

      if (remainingQuantity > 0) {
        const { inspectionData, ...restProduct } = product;
        const remainingProduct = {
          ...restProduct,
          quantity: remainingQuantity,
          uniqueId: `${product.uniqueId}-remaining-${Date.now()}`,
          sourceType: sourceBucket,
        };
        next[sourceBucket] = [...next[sourceBucket], remainingProduct];
      }

      return next;
    });
    setIsInspectionModalVisible(false);
  };

  const moveBackToPending = (product) => {
    setBaskets((previous) => {
      const next = {};
      Object.keys(previous).forEach((key) => {
        next[key] = previous[key].filter((item) => item.uniqueId !== product.uniqueId);
      });
      next.available = [...next.available, product];
      return next;
    });
  };

  const availableBasket = baskets.available || [];
  const goodBasket = baskets.good || [];
  const repackingBasket = baskets.repacking || [];
  const damagedBasket = baskets.damaged || [];
  const scrapBasket = baskets.scrap || [];
  const getQuantityTotal = (items) => items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const totalProducts = orderDetails?.products?.reduce((sum, product) => sum + Number(product.quantity || 0), 0) || 0;
  const pendingQuantity = getQuantityTotal(availableBasket);
  const received = getQuantityTotal(goodBasket) + getQuantityTotal(repackingBasket) + getQuantityTotal(damagedBasket) + getQuantityTotal(scrapBasket);
  const progress = totalProducts ? Math.min(100, Math.round((received / totalProducts) * 100)) : 0;

  const submitBaskets = async () => {
    if (!orderDetails || availableBasket.length > 0) {
      notify("warn", "Inspection incomplete", "Classify every product before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const mapProducts = (items) =>
        items.map((product) => ({
          id: product.uniqueId,
          productId: product.id,
          productUpdatedId: product.updated_id,
          name: product.name,
          sku: product.sku,
          quantity: product.quantity,
          imageUrl: product.imageUrl,
          productLocation: product.productLocation,
          productCategory: product.productCategory,
          ...product.inspectionData,
        }));
      const customer = orderDetails.customer || {};
      const shipmentInfo = orderDetails.shipments?.[0] || {};
      const userName = Cookies.get('userName') || Cookies.get('userEmail') || 'RTO Officer';
      const userEmail = Cookies.get('userEmail') || '';
      const response = await axios.post(`${config.apiBaseUrl}/rto/return-receipt`, {
        orderID: orderDetails.orderID,
        customer: {
          name: customer.name || '',
          email: customer.email || '',
          mobile: customer.mobile || '',
        },
        shipment: {
          awb_code: shipmentInfo.awb_code || '',
          courier_name: shipmentInfo.courier_name || '',
        },
        receivedAt: moment().toISOString(),
        receivedBy: userName,
        receivedByEmail: userEmail,
        userName,
        userEmail,
        source: 'rto-inbound',
        classification: {
          good: mapProducts(goodBasket),
          repacking: mapProducts(repackingBasket),
          damaged: mapProducts(damagedBasket),
          scrap: mapProducts(scrapBasket),
        },
        totalProducts,
      });

      if ([200, 201].includes(response.status)) {
        notify("success", "RTO completed", "The return receipt was submitted successfully.");
        setTimeout(clearOrder, 1200);
      }
    } catch (err) {
      notify("error", "Submission failed", err.response?.data?.message || err.message || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const shipment = orderDetails?.shipments?.[0] || {};
  const customer = orderDetails?.customer || {};
  const statusSteps = [
    { title: "Order created", description: orderDetails?.order_date ? moment(orderDetails.order_date).format("DD MMM YYYY") : "Not available" },
    { title: "Packed", description: orderDetails?.packed_date ? moment(orderDetails.packed_date).format("DD MMM YYYY") : "Not available" },
    { title: "Shipped", description: orderDetails?.warehouse_out_date ? moment(orderDetails.warehouse_out_date).format("DD MMM YYYY") : "Not available" },
    { title: "RTO inspection", description: progress === 100 ? "Ready to submit" : "In progress" },
  ];
  const currentStep = progress === 100 ? 3 : orderDetails?.warehouse_out_date ? 2 : orderDetails?.packed_date ? 1 : 0;

  const renderMeta = (icon, label, value) => (
    <div className="rto-meta-row">
      <span className="rto-meta-icon">{icon}</span>
      <span>
        <Text type="secondary">{label}</Text>
        <Text strong>{value || "N/A"}</Text>
      </span>
    </div>
  );

  const renderBasket = (type, items) => {
    const config = BASKET_CONFIG[type];
    return (
      <section
        className={`rto-basket rto-basket-${type}`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => handleDropInBasket(event, type)}
      >
        <div className="rto-basket-header">
          <span className="rto-basket-icon">{config.icon}</span>
          <span className="rto-basket-title">
            <Text strong>{config.label}</Text>
            <Text type="secondary">{config.helper}</Text>
          </span>
          <Badge count={getQuantityTotal(items)} showZero />
        </div>
        <div className="rto-basket-body">
          {items.length === 0 ? (
            <div className="rto-drop-hint">Drop products here or use a classification button</div>
          ) : (
            items.map((product) => (
              <div
                className="rto-basket-item"
                key={product.uniqueId}
                draggable
                onDragStart={(event) => handleDragStart(event, product, type)}
              >
                <span>
                  <Text strong ellipsis>{product.name}</Text>
                  <Text type="secondary">SKU {product.sku || "N/A"} · Qty {product.quantity}</Text>
                </span>
                <Button
                  type="text"
                  size="small"
                  title="Move back to pending"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => moveBackToPending(product)}
                />
              </div>
            ))
          )}
        </div>
      </section>
    );
  };

  return (
    <main className={`rto-page ${darkTheme ? "dark-theme" : ""}`}>
      <Toast ref={toast} className="rto-toast" />
      {showNetworkBanner && (
        <Alert
          className="rto-network-alert"
          type="warning"
          showIcon
          closable
          message="Network connection lost"
          description="Your current inspection is still visible, but it cannot be submitted while offline."
          onClose={() => setShowNetworkBanner(false)}
        />
      )}

      {orderDetails && (
        <Card className="rto-command-bar">
          <div className="rto-command-title">
            <span className="rto-search-icon"><InboxOutlined /></span>
            <span>
              <Text className="rto-eyebrow">Returns operations</Text>
              <Title level={4}>RTO inbound inspection</Title>
            </span>
          </div>
          <div className="rto-search-controls">
            <Input
              allowClear
              prefix={<BarcodeOutlined />}
              placeholder="Enter Order ID"
              value={searchOrderID}
              disabled={loading}
              onChange={(event) => setSearchOrderID(event.target.value)}
              onPressEnter={searchPastOrder}
            />
            <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={searchPastOrder}>
              Search
            </Button>
            <Button icon={<ClearOutlined />} onClick={clearOrder}>
              Clear order
            </Button>
          </div>
          <div className="rto-topbar-status">
            <Tag color="blue">Order #{orderDetails.orderID || searchOrderID}</Tag>
            <Tag icon={isNetworkLost ? <DisconnectOutlined /> : <WifiOutlined />} color={isNetworkLost ? "error" : "success"}>
              {isNetworkLost ? "Offline" : "Online"}
            </Tag>
            <Tag icon={<ClockCircleOutlined />}>{currentTime}</Tag>
          </div>
        </Card>
      )}

      {loading && !orderDetails ? (
        <div className="rto-loading"><Spin size="large" tip="Loading order details..." /></div>
      ) : !orderDetails ? (
        <div className="rto-premium-landing">
          <div className="rto-landing-orb rto-landing-orb-1" />
          <div className="rto-landing-orb rto-landing-orb-2" />
          <div className="rto-landing-orb rto-landing-orb-3" />
          <div className="rto-landing-content">
            <div className="rto-landing-hero">
              <div className="rto-landing-icon-wrap">
                <div className="rto-landing-icon"><InboxOutlined /></div>
              </div>
              <Title level={2}>RTO Inbound Inspection</Title>
              <Text className="rto-landing-subtitle">
                Search for a return order to begin the inspection and classification process
              </Text>
            </div>
            <div className="rto-landing-search-box">
              <div className="rto-landing-search-inner">
                <span className="rto-landing-search-prefix"><BarcodeOutlined /></span>
                <input
                  className="rto-landing-search-input"
                  placeholder="Enter Order ID..."
                  value={searchOrderID}
                  onChange={(event) => setSearchOrderID(event.target.value)}
                  onKeyPress={(event) => event.key === "Enter" && searchPastOrder()}
                  autoFocus
                />
                <button
                  className="rto-landing-search-btn"
                  onClick={searchPastOrder}
                  disabled={loading}
                >
                  {loading ? <Spin size="small" /> : <><SearchOutlined /> Search</>}
                </button>
              </div>
            </div>
            <div className="rto-landing-features">
              <div className="rto-feature-card">
                <div className="rto-feature-icon"><SearchOutlined /></div>
                <Text strong>Quick Lookup</Text>
                <Text type="secondary">Find return orders instantly by ID</Text>
              </div>
              <div className="rto-feature-card">
                <div className="rto-feature-icon"><BoxPlotOutlined /></div>
                <Text strong>Inspect Products</Text>
                <Text type="secondary">Classify each product condition</Text>
              </div>
              <div className="rto-feature-card">
                <div className="rto-feature-icon"><TruckOutlined /></div>
                <Text strong>Track Shipments</Text>
                <Text type="secondary">View courier and AWB details</Text>
              </div>
              <div className="rto-feature-card">
                <div className="rto-feature-icon"><CheckCircleOutlined /></div>
                <Text strong>Complete RTO</Text>
                <Text type="secondary">Submit inspection receipt</Text>
              </div>
            </div>
            <div className="rto-landing-hint">
              <Text type="secondary">
                Press <Tag color="blue">Enter</Tag> or click Search to find an order
              </Text>
            </div>
          </div>
        </div>
      ) : (
        <>
          <section className="rto-order-strip">
            <div>
              <Text className="rto-eyebrow">Active return</Text>
              <Title level={3}>Order #{orderDetails.orderID || searchOrderID}</Title>
              <Text type="secondary">{customer.name || "Customer details unavailable"} · {shipment.courier_name || "Courier unavailable"}</Text>
            </div>
            <div className="rto-progress-block">
              <div><Text strong>Inspection progress</Text><Text strong>{received}/{totalProducts}</Text></div>
              <Progress percent={progress} showInfo={false} strokeColor="#2563eb" />
            </div>
            <Button icon={<ClearOutlined />} onClick={clearOrder}>Clear order</Button>
          </section>

          <div className="rto-workspace">
            <aside className="rto-sidebar">
              <Card className="rto-panel rto-details-card">
                <div className="rto-panel-heading">
                  <Text className="rto-eyebrow">Order information</Text>
                 
                </div>
                <Tabs
                  className="rto-details-tabs"
                  defaultActiveKey="customer"
                  items={[
                    {
                      key: "customer",
                      label: <span><UserOutlined /> Customer</span>,
                      children: (
                        <div className="rto-tab-content">
                          <div className="rto-customer">
                            <Avatar size={48} icon={<UserOutlined />} />
                            <span>
                              <Text strong>{customer.name || "Unknown customer"}</Text>
                              <Text type="secondary">{customer.email || customer.mobile || "No contact details"}</Text>
                            </span>
                          </div>
                          {customer.tags?.length > 0 && (
                            <div className="rto-tags">
                              {customer.tags.map((tag, index) => <Tag key={index}>{typeof tag === "string" ? tag : tag.name}</Tag>)}
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      key: "shipment",
                      label: <span><TruckOutlined /> Shipment</span>,
                      children: (
                        <div className="rto-meta-grid rto-tab-content">
                          {renderMeta(<BarcodeOutlined />, "AWB", shipment.awb_code)}
                          {renderMeta(<TruckOutlined />, "Courier", shipment.courier_name)}
                          {renderMeta(<CalendarOutlined />, "Order date", orderDetails.order_date ? moment(orderDetails.order_date).format("DD MMM YYYY") : null)}
                          {renderMeta(<UserOutlined />, "Packed by", orderDetails.packed_by)}
                          {renderMeta(<EnvironmentOutlined />, "Warehouse", orderDetails.warehouse)}
                        </div>
                      ),
                    },
                    {
                      key: "journey",
                      label: <span><ClockCircleOutlined /> Journey</span>,
                      children: (
                        <div className="rto-tab-content rto-timeline-card">
                          <Steps direction="vertical" size="small" current={currentStep} items={statusSteps} />
                        </div>
                      ),
                    },
                  ]}
                />
              </Card>
            </aside>

            <section className="rto-products-panel">
              <div className="rto-section-heading">
                <span>
                  <Text className="rto-eyebrow">Step 1</Text>
                  <Title level={4}>Inspect pending products</Title>
                  <Text type="secondary">Drag a product to a lane or choose its condition.</Text>
                </span>
                <div className="rto-heading-actions">
                  <div className="rto-inline-progress">
                    <span><Text type="secondary">Progress</Text><Text strong>{received}/{totalProducts}</Text></span>
                    <Progress percent={progress} showInfo={false} strokeColor="#2563eb" size="small" />
                  </div>
                  <Badge count={getQuantityTotal(availableBasket)} showZero overflowCount={999} />
                </div>
              </div>

              {availableBasket.length === 0 ? (
                <div className="rto-products-empty">
                  <Empty description="All products have been classified" />
                </div>
              ) : (
                <div className="rto-product-list">
                  {availableBasket.map((product, index) => (
                    <article
                      className="rto-product-card"
                      key={product.uniqueId}
                      draggable
                      onDragStart={(event) => handleDragStart(event, product, "available")}
                    >
                      <span className="rto-product-number">{String(index + 1).padStart(2, "0")}</span>
                      <Image
                        preview={false}
                        src={product.imageUrl}
                        fallback="https://via.placeholder.com/88?text=No+Image"
                        alt={product.name}
                      />
                      <div className="rto-product-info">
                        <Title level={5} ellipsis={{ rows: 1 }}>{product.name}</Title>
                        <div className="rto-product-tags">
                          <Tag>SKU {product.sku || "N/A"}</Tag>
                          <Tag>Qty {product.quantity}</Tag>
                          <Tag>{product.productLocation || "No location"}</Tag>
                          <Tag>{product.productCategory || "Uncategorized"}</Tag>
                        </div>
                        <div className="rto-classify-actions">
                          {Object.entries(BASKET_CONFIG).map(([type, config]) => (
                            <Button key={type} className={`rto-action-${type}`} onClick={() => openInspection(product, type)}>
                              {config.icon} {config.shortLabel}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <aside className="rto-routing-panel">
              <div className="rto-section-heading">
                <span>
                  <Text className="rto-eyebrow">Step 2</Text>
                  <Title level={4}>Route inspected products</Title>
                </span>
              </div>
              <div className="rto-summary-grid">
                <div><Text type="secondary">Pending</Text><strong>{getQuantityTotal(availableBasket)}</strong></div>
                <div><Text type="secondary">Restock</Text><strong>{getQuantityTotal(goodBasket)}</strong></div>
                <div><Text type="secondary">Repack</Text><strong>{getQuantityTotal(repackingBasket)}</strong></div>
                <div><Text type="secondary">Damaged</Text><strong>{getQuantityTotal(damagedBasket)}</strong></div>
                <div><Text type="secondary">Scrap</Text><strong>{getQuantityTotal(scrapBasket)}</strong></div>
              </div>
              <div className="rto-baskets">
                {renderBasket("good", goodBasket)}
                {renderBasket("repacking", repackingBasket)}
                {renderBasket("damaged", damagedBasket)}
                {renderBasket("scrap", scrapBasket)}
              </div>
              <div className="rto-submit-bar">
                <Text type="secondary">
                  {pendingQuantity > 0 ? `${pendingQuantity} item${pendingQuantity === 1 ? "" : "s"} still need inspection` : "Inspection complete and ready to submit"}
                </Text>
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  loading={submitting}
                  disabled={pendingQuantity > 0 || isNetworkLost}
                  onClick={submitBaskets}
                >
                  Complete RTO
                </Button>
              </div>
            </aside>
          </div>
        </>
      )}

      <InspectionFormModal
        visible={isInspectionModalVisible}
        onCancel={() => setIsInspectionModalVisible(false)}
        onSave={handleInspectionSave}
        product={inspectionProduct}
        basketType={inspectionBasketType}
        sourceBasket={inspectionSourceType}
        darkTheme={darkTheme}
      />
    </main>
  );
};

export default RTOInboundPage;
